import React from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Building2, FileText, Eye, Upload, Trash2 } from 'lucide-react';
import { API_URL, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { ProposalStatusBadge } from './ProposalStatusBadge';

const ProposalMetaGrid = ({ proposal }) => (
  <div className="grid grid-cols-2 gap-4">
    <div>
      <label className="text-sm font-medium text-muted-foreground">Client Contact</label>
      <p className="font-medium">{proposal.client_name}</p>
    </div>
    {proposal.company_name && (
      <div>
        <label className="text-sm font-medium text-muted-foreground">Company</label>
        <p className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4" />{proposal.company_name}</p>
      </div>
    )}
    {proposal.category && (
      <div>
        <label className="text-sm font-medium text-muted-foreground">Category</label>
        <p className="font-medium">{proposal.category}</p>
      </div>
    )}
    {proposal.amount && (
      <div>
        <label className="text-sm font-medium text-muted-foreground">Amount</label>
        <p className="font-medium font-mono">INR {proposal.amount.toLocaleString('en-IN')}</p>
      </div>
    )}
    {proposal.total_area_sqmt && (
      <div>
        <label className="text-sm font-medium text-muted-foreground">Total Area (sq.ft.)</label>
        <p className="font-medium">{proposal.total_area_sqmt.toLocaleString()} sq.ft.</p>
      </div>
    )}
    <div>
      <label className="text-sm font-medium text-muted-foreground">Version</label>
      <p className="font-medium">v{proposal.version || 1}</p>
    </div>
  </div>
);

const DocumentList = ({ proposal, canDelete, onDeleted }) => {
  if (!proposal.documents || proposal.documents.length === 0) return null;
  const handleDelete = async (idx) => {
    try {
      await axios.delete(`${API_URL}/proposals/${proposal.proposal_id}/documents/${idx}`, { withCredentials: true });
      toast.success('Document deleted');
      onDeleted(idx);
    } catch {
      toast.error('Failed to delete document');
    }
  };
  return (
    <div>
      <label className="text-sm font-medium text-muted-foreground">Uploaded Documents</label>
      <div className="space-y-1 mt-1">
        {proposal.documents.map((doc, idx) => (
          <div key={doc.file_id || `doc-${idx}`} className="p-2 bg-muted rounded flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm truncate flex-1">{doc.original_filename || doc.filename}</span>
            <span className="text-xs text-muted-foreground">{formatDate(doc.uploaded_at)}</span>
            {doc.view_link && (
              <a href={doc.view_link} target="_blank" rel="noopener noreferrer" title="View document" data-testid={`view-doc-${idx}`}>
                <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </a>
            )}
            {canDelete && (
              <button
                onClick={() => handleDelete(idx)}
                title="Delete document"
                data-testid={`delete-doc-${idx}`}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const ViewProposalDialog = ({ open, onOpenChange, proposal, setViewingProposal, currentUser, onNavigate, onUploadPdf, onRefresh }) => {
  if (!proposal) return null;
  const canDelete = ['admin', 'manager'].includes(currentUser?.role);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">{proposal.title}</DialogTitle>
            <ProposalStatusBadge status={proposal.status} />
          </div>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <ProposalMetaGrid proposal={proposal} />

          {proposal.requirement && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Requirement</label>
              <p className="font-medium">{proposal.requirement}</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-muted-foreground">Description</label>
            <p className="font-medium">{proposal.description}</p>
          </div>

          {proposal.drive_file_name && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Attached Document</label>
              <div className="p-2 bg-muted rounded flex items-center gap-2 mt-1">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{proposal.drive_file_name}</span>
                {proposal.drive_file_link && (
                  <a href={proposal.drive_file_link} target="_blank" rel="noopener noreferrer" className="ml-auto">
                    <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </a>
                )}
              </div>
            </div>
          )}

          <DocumentList
            proposal={proposal}
            canDelete={canDelete}
            onDeleted={(idx) => {
              onRefresh();
              setViewingProposal((prev) => prev ? ({
                ...prev,
                documents: prev.documents.filter((_, i) => i !== idx)
              }) : prev);
            }}
          />

          <div className="grid grid-cols-3 gap-4 pt-2 border-t">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Drafted</label>
              <p className="font-medium text-sm">{formatDate(proposal.drafted_date || proposal.created_at)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Sent to Client</label>
              <p className="font-medium text-sm">{proposal.sent_date ? formatDate(proposal.sent_date) : '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Signed / Rejected</label>
              <p className="font-medium text-sm">{formatDate(proposal.signed_date || proposal.rejected_date) || '-'}</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf,.doc,.docx';
                input.onchange = (e) => {
                  if (e.target.files[0]) onUploadPdf(proposal.proposal_id, e.target.files[0]);
                };
                input.click();
              }}
              data-testid="upload-pdf-from-view"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload PDF
            </Button>
            <Button variant="outline" onClick={() => onNavigate(`/proposals/${proposal.proposal_id}`)}>
              View Full Details
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
